import { lazy, Suspense, useEffect, useMemo, useState } from "react";
const WrittenHomeworks=lazy(()=>import("./WrittenHomeworks"));
import { ArrowLeft, BookOpen, CalendarDays, ChevronDown, ClipboardList, ListChecks, UserCheck } from "lucide-react";
import { ActivityDefinition, CatalogCompetency, EvaluationLevel, SchoolLevel } from "./catalog";
import { StudentLibrary } from "./StudentLibrary";
import { useActivityLibrary } from "./activity-library-client";
import { digitalActivitySummaryForSession } from "./student-dashboard-results";

type Level = EvaluationLevel;
type Period = 1 | 2 | 3;
type PeriodFilter = "all" | Period;

type DashboardStudent = {
  id: string;
  firstName: string;
  lastName: string;
  results: Record<string, Level | "NE" | "ABS">;
  resultUpdatedAt?: Record<string, string>;
};

type DashboardSession = {
  digitalSessionId?: string;
  id: string;
  classId: string;
  activityId: string;
  startedAt: string;
  endedAt?: string;
  period?: Period;
  participation?: Record<string, Level>;
  absentIds?: string[];
  evaluations?: Record<string, Record<string, Level | "NE" | "ABS">>;
};

type QcmScore = {
  sessionId: string;
  mode: "prerequisite" | "evaluated";
  theme: string;
  chapter: string;
  correctCount: number;
  totalCount: number;
  score20: number | null;
  mastery: Level | null;
  period: Period;
  date: string;
};

type Props = {
  student: DashboardStudent;
  classId: string;
  className: string;
  schoolLevel: SchoolLevel;
  avatarUrl: string;
  catalog: CatalogCompetency[];
  activities: ActivityDefinition[];
  sessions: DashboardSession[];
  onBack: () => void;
  hideBack?: boolean;
  teacherNavigation?: boolean;
};

type Observation = { level: Level; value: number; weight: number; activityId: string; date: number };
type CompetencyStats = { score?: number; observations: Observation[]; counts: Record<Level, number> };

const levels: Level[] = ["A", "B", "C", "D"];
const scores: Record<Level, number> = { A: 20, B: 13.3, C: 6.7, D: 0 };
const emptyCounts = (): Record<Level, number> => ({ A: 0, B: 0, C: 0, D: 0 });
const displayScore = (value?: number | null) => value == null ? "—" : `${value.toFixed(1).replace(".0", "")} / 20`;

function Distribution({ stats }: { stats: CompetencyStats }) {
  return <div className="competency-distribution" aria-label={`${stats.observations.length} évaluations`}>
    {levels.map((level) => <span className={`count-${level.toLowerCase()}`} key={level}><b>{level}</b>{stats.counts[level]}</span>)}
    <small>{stats.observations.length} éval.</small>
  </div>;
}

export function StudentDashboard({ student, classId, className, schoolLevel, avatarUrl, catalog, activities, sessions, onBack, hideBack = false, teacherNavigation = false }: Props) {
  const [tab, setTab] = useState<"written" | "competencies" | "activities" | "qcm" | "binder" | "todo" | "progress" | "completed" | "overview" | "participation">(teacherNavigation ? "overview" : "todo");
  const library = useActivityLibrary({classId,studentId:student.id});
  const [period, setPeriod] = useState<PeriodFilter>("all");
  const [qcmScores, setQcmScores] = useState<QcmScore[]>([]);
  const activityById = useMemo(() => new Map(activities.map((activity) => [activity.id, activity])), [activities]);
  const validCompetencyIds = useMemo(() => new Set(catalog.map((item) => item.id)), [catalog]);
  const classSessions = useMemo(() => sessions
    .filter((session) => session.classId === classId)
    .filter((session) => period === "all" || (session.period ?? 1) === period)
    .sort((a, b) => Date.parse(a.startedAt) - Date.parse(b.startedAt)), [sessions, classId, period]);
  const visibleSessions = useMemo(() => classSessions.filter((session) => !session.activityId || activityById.has(session.activityId)), [classSessions, activityById]);

  function visibleResultKey(key: string) {
    const separator = key.lastIndexOf(":");
    if (separator < 0) return false;
    const activityId = key.slice(0, separator);
    const competencyId = key.slice(separator + 1);
    return activityById.has(activityId) && validCompetencyIds.has(competencyId);
  }

  useEffect(() => {
    let cancelled = false;
    fetch(`/api/qcm-results/student-history?classId=${encodeURIComponent(classId)}&studentId=${encodeURIComponent(student.id)}`, { credentials: "include" })
      .then((response) => response.ok ? response.json() : { scores: [] })
      .then((body) => { if (!cancelled) setQcmScores(Array.isArray(body.scores) ? body.scores : []); })
      .catch(() => { if (!cancelled) setQcmScores([]); });
    return () => { cancelled = true; };
  }, [classId, student.id]);

  const resultRecords = useMemo(() => {
    const records = new Map<string, { level: Level; date: number }>();
    visibleSessions.forEach((session) => Object.entries(session.evaluations?.[student.id] ?? {}).forEach(([key, level]) => {
      if (!visibleResultKey(key) || level === "NE" || level === "ABS") return;
      records.set(key, { level, date: Date.parse(session.endedAt ?? session.startedAt) || 0 });
    }));
    if (period === "all" || period === 1) {
      Object.entries(student.results ?? {}).forEach(([key, level]) => {
        if (!visibleResultKey(key) || level === "NE" || level === "ABS") return;
        const date = Date.parse(student.resultUpdatedAt?.[key] ?? "") || 0;
        if (!records.has(key) || date >= records.get(key)!.date) records.set(key, { level, date });
      });
    }
    return records;
  }, [visibleSessions, period, student.id, student.results, student.resultUpdatedAt, activityById, validCompetencyIds]);

  function statsFor(ids: Set<string>): CompetencyStats {
    const observations: Observation[] = [];
    resultRecords.forEach((record, key) => {
      const separator = key.lastIndexOf(":");
      if (separator < 0 || !ids.has(key.slice(separator + 1))) return;
      const activityId = key.slice(0, separator);
      const activity = activityById.get(activityId);
      if (!activity) return;
      observations.push({ level: record.level, value: scores[record.level], weight: activity.coefficient ?? 1, activityId, date: record.date });
    });
    const counts = emptyCounts();
    if (period === "all") for (const event of library.data?.state.history ?? []) {
      if (event.source === "legacy_session" || !ids.has(event.competencyId) || !levels.includes(event.level as Level)) continue;
      const level = event.level as Level;
      const version = library.data?.state.versions.find(v=>v.id===event.activityId && v.version===event.activityVersion);
      observations.push({level,value:scores[level],weight:version?.coefficient ?? 1,activityId:event.activityId,date:Date.parse(event.validatedAt)});
    }
    observations.forEach((item) => { counts[item.level] += 1; });
    const totalWeight = observations.reduce((sum, item) => sum + item.weight, 0);
    const score = totalWeight ? observations.reduce((sum, item) => sum + item.value * item.weight, 0) / totalWeight : undefined;
    return { score, observations, counts };
  }

  const levelCatalog = catalog.filter((item) => item.schoolLevel === schoolLevel);
  const generalRows = levelCatalog.filter((item) => !item.parentId).map((general) => {
    const equivalentGenerals = catalog.filter((item) => !item.parentId && item.code === general.code);
    const parentIds = new Set(equivalentGenerals.map((item) => item.id));
    const axisIds = new Set(catalog.filter((item) => parentIds.has(item.parentId ?? "") || parentIds.has(item.id)).map((item) => item.id));
    const details = levelCatalog.filter((item) => item.parentId === general.id).map((detail) => {
      const equivalentIds = new Set(catalog.filter((item) => item.code === detail.code).map((item) => item.id));
      return { competency: detail, stats: statsFor(equivalentIds) };
    });
    return { competency: general, stats: statsFor(axisIds), details };
  });

  const evaluatedGeneral = generalRows.filter((row) => row.stats.score !== undefined);
  const overallScore = evaluatedGeneral.length ? evaluatedGeneral.reduce((sum, row) => sum + row.stats.score!, 0) / evaluatedGeneral.length : undefined;
  const recordedSessions = visibleSessions.filter((session) => session.participation?.[student.id]);
  const absences = recordedSessions.filter((session) => session.absentIds?.includes(student.id)).length;
  const presentSessions = recordedSessions.filter((session) => !session.absentIds?.includes(student.id));
  const participationScore = presentSessions.length ? presentSessions.reduce((sum, session) => sum + scores[session.participation![student.id]], 0) / presentSessions.length : undefined;
  const activitySessions = [...recordedSessions].sort((a, b) => Date.parse(b.startedAt) - Date.parse(a.startedAt));
  const visibleQcmScores = qcmScores.filter((item) => period === "all" || item.period === period);
  const evaluatedQcmScores = visibleQcmScores.filter((item) => item.mode === "evaluated" && item.score20 !== null);
  const qcmAverage = evaluatedQcmScores.length
    ? evaluatedQcmScores.reduce((sum, item) => sum + (item.score20 ?? 0), 0) / evaluatedQcmScores.length
    : undefined;

  return <main className="student-dashboard">
    {!hideBack && <button className="dashboard-back" onClick={onBack}><ArrowLeft size={17} /> Retour au trombinoscope</button>}
    <header className="student-dashboard-head">
      <img src={avatarUrl} alt={`Portrait de ${student.firstName} ${student.lastName}`} />
      <div><span>{className}</span><h1>{student.firstName} {student.lastName}</h1><p>Tableau de bord pédagogique</p></div>
    </header>

    <div className="dashboard-periods" role="group" aria-label="Période affichée">
      {(["all", 1, 2, 3] as PeriodFilter[]).map((value) => <button className={period === value ? "active" : ""} key={value} onClick={() => setPeriod(value)}>{value === "all" ? "Année" : `Période ${value}`}</button>)}
    </div>

    <section className="dashboard-kpis">
      <article><ClipboardList size={20} /><span>Compétences</span><strong>{displayScore(overallScore)}</strong><small>{evaluatedGeneral.length} axe{evaluatedGeneral.length > 1 ? "s" : ""} évalué{evaluatedGeneral.length > 1 ? "s" : ""}</small></article>
      <article><UserCheck size={20} /><span>Participation active</span><strong>{displayScore(participationScore)}</strong><small>{presentSessions.length} séance{presentSessions.length > 1 ? "s" : ""} prise{presentSessions.length > 1 ? "s" : ""} en compte</small></article>
      <article><ListChecks size={20} /><span>QCM évalués</span><strong>{displayScore(qcmAverage)}</strong><small>{evaluatedQcmScores.length} QCM noté{evaluatedQcmScores.length > 1 ? "s" : ""}</small></article>
      <article><CalendarDays size={20} /><span>Absences</span><strong>{absences}</strong><small>sur {recordedSessions.length} séance{recordedSessions.length > 1 ? "s" : ""}</small></article>
    </section>

    <nav className="dashboard-tabs" aria-label="Fiche élève">
      {!teacherNavigation&&<button className={tab==="written"?"active":""} onClick={()=>setTab("written")}>Mes devoirs écrits</button>}
      {teacherNavigation && <button className={tab === "overview" ? "active" : ""} onClick={()=>setTab("overview")}>Vue générale</button>}
      {(["todo","binder"] as const).map(value=><button key={value} className={tab===value?"active":""} onClick={()=>setTab(value)}>{({todo:"À faire",progress:"En cours",completed:"Terminées",binder:"Classeur"})[value]}</button>)}
      <button className={tab === "competencies" ? "active" : ""} onClick={() => setTab("competencies")}><ClipboardList size={18} /> Compétences</button>
      <button className={tab === "activities" ? "active" : ""} onClick={() => setTab("activities")}><BookOpen size={18} /> {teacherNavigation ? "Historique" : "Activités"}</button>
      {!teacherNavigation && <a href="/qcm-training">Entraînement QCM</a>}
      <button className={tab === "qcm" ? "active" : ""} onClick={() => setTab("qcm")}><ListChecks size={18} /> QCM</button>
      {teacherNavigation && <button className={tab === "participation" ? "active" : ""} onClick={()=>setTab("participation")}>Participation</button>}
    </nav>

    {tab === "written" ? <Suspense fallback={<p>Chargement…</p>}><WrittenHomeworks student/></Suspense> : tab === "overview" ? <section className="dashboard-panel"><p>Les indicateurs ci-dessus résument le parcours. Consulte le travail en cours, le classeur et l’historique depuis cette fiche.</p></section> : tab === "binder" || tab === "todo" || tab === "progress" || tab === "completed" ? <StudentLibrary key={tab} student={{classId,studentId:student.id}} initialTab={tab} hideNavigation /> : tab === "competencies" ? <section className="competency-table dashboard-panel">
      <header><div><span>Compétence</span><span>Validations</span><span>Note coefficientée</span></div></header>
      {generalRows.map(({ competency: general, stats, details }) => <details className="general-competency-row" key={general.id}>
        <summary><ChevronDown size={18} /><div className="competency-name"><strong>{general.code}</strong><span>{general.label}</span></div><Distribution stats={stats} /><b>{displayScore(stats.score)}</b></summary>
        <div className="linked-competencies">
          {details.length ? details.map(({ competency: detail, stats: detailStats }) => <details className="detail-competency-row" key={detail.id}>
            <summary><ChevronDown size={16} /><div className="competency-name"><strong>{detail.code}</strong><span>{detail.label}</span></div><Distribution stats={detailStats} /><b>{displayScore(detailStats.score)}</b></summary>
            <div className="validation-criteria-list">{levels.map((level) => <div className={`criterion-line criterion-${level.toLowerCase()}`} key={level}><strong>{level}</strong><p>{detail.criteria[level] || "Critère à définir"}</p><span>{detailStats.counts[level]} fois</span></div>)}</div>
          </details>) : <div className="validation-criteria-list general-criteria">{levels.map((level) => <div className={`criterion-line criterion-${level.toLowerCase()}`} key={level}><strong>{level}</strong><p>{general.criteria[level] || "Critère à définir"}</p><span>{stats.counts[level]} fois</span></div>)}</div>}
        </div>
      </details>)}
      {!generalRows.length && <p className="dashboard-empty">Aucune compétence pour ce niveau.</p>}
    </section> : tab === "activities" || tab === "participation" ? <section className="activity-session-list">
      {activitySessions.map((session) => {
        const absent = session.absentIds?.includes(student.id);
        const participation = session.participation?.[student.id];
        const activity = activityById.get(session.activityId);
        const digitalSummary = digitalActivitySummaryForSession(library.data?.state, session.digitalSessionId, catalog);
        const legacyResults = Object.entries(session.evaluations?.[student.id] ?? {}).flatMap(([key, level]) => {
          if (!visibleResultKey(key)) return [];
          const competencyId = key.slice(key.lastIndexOf(":") + 1);
          const competency = catalog.find((item) => item.id === competencyId);
          return competency ? [{ competency, level }] : [];
        });
        const results = digitalSummary?.results ?? legacyResults;
        const scored = results.filter((r): r is typeof r & {level: Level} => r.level !== "NE" && r.level !== "ABS");
        const legacyActivityScore = scored.length ? scored.reduce((sum, result) => sum + scores[result.level], 0) / scored.length : undefined;
        const activityScore = digitalSummary ? digitalSummary.grade ?? undefined : legacyActivityScore;
        const activityTitle = digitalSummary?.title ?? activity?.title ?? "Séance sans activité";
        const activityChapter = digitalSummary?.chapter ?? activity?.chapter ?? "Participation active uniquement";
        return <details className="session-activity-card dashboard-panel" key={session.id}>
          <summary><div className="session-date"><time>{new Date(session.startedAt).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })}</time><small>Période {session.period ?? 1}</small></div><div className="session-main"><strong>{activityTitle}</strong><span>{activityChapter}</span></div><div className="session-grade"><small>Participation active</small><b className={absent ? "grade-absent" : `level-${participation?.toLowerCase()}`}>{absent ? "ABS" : participation ?? "—"}</b></div><div className="session-grade"><small>Activité</small><strong>{displayScore(activityScore)}</strong></div><ChevronDown size={19} /></summary>
          <div className="session-details">
            <div className="participation-detail"><UserCheck size={18} /><div><strong>Participation active : {absent ? "Absent" : `${participation} — ${displayScore(participation ? scores[participation] : undefined)}`}</strong><span>Évaluée systématiquement pour cette séance.</span></div></div>
            {results.length ? <div className="session-competencies"><h3>Compétences de l’activité</h3>{results.map(({ competency, level }) => <div key={competency.id}><span><strong>{competency.code}</strong>{competency.label}</span><b className={`level-${level.toLowerCase()}`}>{level}</b></div>)}</div> : <p className="dashboard-empty">Aucune compétence évaluée pendant cette séance.</p>}
          </div>
        </details>;
      })}
      {!activitySessions.length && <div className="dashboard-panel"><p className="dashboard-empty">Aucune séance enregistrée pour cette période.</p></div>}
    </section> : <section className="activity-session-list qcm-dashboard-list">
      {visibleQcmScores.map((score) => <article className="dashboard-panel qcm-dashboard-score" key={score.sessionId}>
        <div className="session-date"><time>{new Date(score.date).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })}</time><small>{score.mode === "evaluated" ? "QCM évalué" : "Prérequis"} · Période {score.period}</small></div>
        <div className="session-main"><strong>{score.chapter}</strong><span>{score.theme || "Sans thème"}</span></div>
        <div className="session-grade"><small>Réussite</small><strong>{score.correctCount} / {score.totalCount}</strong></div>
        <div className="session-grade"><small>Note QCM</small><strong>{score.mode === "evaluated" ? displayScore(score.score20) : "Non noté"}</strong></div>
        {score.mastery ? <b className={`level-${score.mastery.toLowerCase()}`}>{score.mastery}</b> : <b>Non noté</b>}
      </article>)}
      {!visibleQcmScores.length && <div className="dashboard-panel"><p className="dashboard-empty">Aucun QCM enregistré pour cette période.</p></div>}
      <div className="dashboard-panel"><p className="dashboard-empty">Les réponses question par question restent privées côté professeur. Les prérequis affichent uniquement le total de réussite ; seuls les QCM évalués ont une note.</p></div>
    </section>}
  </main>;
}
